import { useEffect, useState, useRef, useCallback } from "react";
import { Tabs, Tab, Box, Button, CircularProgress, DialogContent, Dialog } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { UpdateAndGetResponse, GetDataFromApiWithParams } from "../../../../../../services/api/ExecuteApiRequests";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlanPrintPreview3 from "../../../MasterPlanandLandBankView/Forms/PlanPrintPreview3";
import PlanPrintPreview4 from "../../../MasterPlanandLandBankView/Forms/PlanPrintPreview4"; // Fixed import path
import MapDataProvider from "../../MapRelated/MapData/MapDataProvider";
import IPDCFinalTable from "../../../../../../components/IPDCFinalTable";

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`plan-tabpanel-${index}`}
      aria-labelledby={`plan-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function PlanRequestsManager() {
  const [tabValue, setTabValue] = useState(0);
  const [approvedPlans, setApprovedPlans] = useState([]);
  const [declinedRequests, setDeclinedRequests] = useState([]);
  const [loadingApproved, setLoadingApproved] = useState(true);
  const [loadingDeclined, setLoadingDeclined] = useState(true);
  const [loadingRows, setLoadingRows] = useState({});
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedInvestorName, setSelectedInvestorName] = useState("");
  const [selectedParkName, setSelectedParkName] = useState(null);
  const [loadingGeometry, setLoadingGeometry] = useState(false);
  const [previewType, setPreviewType] = useState("approved"); // "approved" or "declined"
  const componentRef = useRef();

  // Utility: convert nested objects to strings
  const stringifyNestedObjects = (dataArray) => {
    if (!dataArray || !Array.isArray(dataArray)) return [];
    
    return dataArray.map(item => {
      const newItem = {};
      Object.keys(item).forEach(key => {
        newItem[key] = (typeof item[key] === "object" && item[key] !== null)
          ? JSON.stringify(item[key])
          : item[key];
      });
      return newItem;
    });
  };

  // Handle view plan for Approved tab
  const handleViewApprovedPlan = async (row) => {
    console.log("Viewing approved plan:", row);
    setPreviewType("approved");
    await loadParcelData(row);
  };

  // Handle view plan for Declined tab
  const handleViewDeclinedPlan = async (row) => {
    console.log("Viewing declined plan:", row);
    setPreviewType("declined");
    await loadParcelData(row);
  };

  // Common function to load parcel data
  const loadParcelData = async (row) => {
    setLoadingGeometry(true);
    
    try {
      const parcelData = await GetDataFromApiWithParams(`parcels/${row.upin}`);
      console.log("Parcel data:", parcelData);
      
      if (parcelData && parcelData[0] && parcelData[0].geometry) {
        setSelectedFeature(parcelData);
        
        setSelectedInvestorName(row.tenant_id || "Investor name not stated");
        setSelectedParkName(row.Park_name || "Park name not stated");
        setOpenPreview(true);
      } else {
        toast.error("No geometry data available for this parcel", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error fetching parcel data:", error);
      toast.error("Failed to load parcel data. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoadingGeometry(false);
    }
  };

  // Handle close preview
  const handleClosePreview = () => {
    setOpenPreview(false);
    setTimeout(() => {
      setSelectedFeature(null);
      setSelectedInvestorName("");
      setPreviewType("approved"); // Reset to default
    }, 300);
  };

  // Handle print
  const handlePrint = async () => {
    console.log("Printing plan...");
  };

  // Add action columns for Approved plans (view only)
  const addApprovedActionColumns = useCallback((dataArray) => {
    if (!dataArray || !Array.isArray(dataArray)) return [];
    
    return dataArray.map(item => {
      const { attachments, ...itemWithoutAttachments } = item;
      return {
        ...itemWithoutAttachments,
        viewPlan: (
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewApprovedPlan(itemWithoutAttachments)}
            sx={{ 
              whiteSpace: 'nowrap', 
              minWidth: '100px',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1565c0',
              }
            }}
          >
            View Plan
          </Button>
        )
      };
    });
  }, []);

  // Add action columns for Declined requests (view only)
  const addDeclinedActionColumns = useCallback((dataArray) => {
    if (!dataArray || !Array.isArray(dataArray)) return [];
    
    return dataArray.map(item => {
      const { attachments, ...itemWithoutAttachments } = item;
      return {
        ...itemWithoutAttachments,
        viewPlan: (
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewDeclinedPlan(itemWithoutAttachments)}
            sx={{ 
              whiteSpace: 'nowrap', 
              minWidth: '100px',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1565c0',
              }
            }}
          >
            View Plan
          </Button>
        ),
        status: (
          <Box sx={{ 
            color: '#d32f2f', 
            fontWeight: 'bold',
            backgroundColor: '#ffebee',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'inline-block'
          }}>
            Declined
          </Box>
        )
      };
    });
  }, []);

  // Fetch Approved Plans
  const fetchApprovedPlans = async () => {
    setLoadingApproved(true);
    try {
      const result = await GetDataFromApiWithParams("investorplanrequests/approved/approved");
      
      if (result && result.success && result.data && result.data.length > 0) {
        const formattedData = stringifyNestedObjects(result.data);
        const dataWithActions = addApprovedActionColumns(formattedData);
        setApprovedPlans(dataWithActions);
        
        console.log("Fetched approved plans:", dataWithActions);
      } else {
        console.log("No approved plans found");
        setApprovedPlans([]);
      }
    } catch (error) {
      console.error("Error fetching approved plans:", error);
      toast.error("Failed to load approved plans. Please try again.", {
        position: "top-right",
        autoClose: 8000
      });
    } finally {
      setLoadingApproved(false);
    }
  };

  // Fetch Declined Requests
  const fetchDeclinedRequests = async () => {
    setLoadingDeclined(true);
    try {
      const result = await GetDataFromApiWithParams("investorplanrequests/declined/declined");
      
      if (result && result.success && result.data && result.data.length > 0) {
        const formattedData = stringifyNestedObjects(result.data);
        const dataWithActions = addDeclinedActionColumns(formattedData);
        setDeclinedRequests(dataWithActions);
        
        console.log("Fetched declined requests:", dataWithActions);
      } else {
        console.log("No declined requests found");
        setDeclinedRequests([]);
      }
    } catch (error) {
      console.error("Error fetching declined requests:", error);
      toast.error("Failed to load declined requests. Please try again.", {
        position: "top-right",
        autoClose: 8000
      });
    } finally {
      setLoadingDeclined(false);
    }
  };

  // Get columns for Approved plans
  const getApprovedColumns = (data) => {
    if (!data || data.length === 0) return [];
    
    const baseColumns = Object.keys(data[0]).filter(col => !['viewPlan'].includes(col));
    const orderedColumns = [
      'tenant_id',
      'park_id', 
      'upin',
      'description',
      'request_date',
      'approval_date',
      'viewPlan'
    ];
    
    return orderedColumns.filter(col => baseColumns.includes(col) || col === 'viewPlan');
  };

  // Get columns for Declined requests
  const getDeclinedColumns = (data) => {
    if (!data || data.length === 0) return [];
    
    const baseColumns = Object.keys(data[0]).filter(col => !['viewPlan', 'status'].includes(col));
    const orderedColumns = [
      'tenant_id',
      'park_id', 
      'upin',
      'description',
      'request_date',
      'declined_date',
      'status',
      'viewPlan'
    ];
    
    return orderedColumns.filter(col => baseColumns.includes(col) || col === 'viewPlan' || col === 'status');
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    fetchApprovedPlans();
    fetchDeclinedRequests();
  }, []);

  const approvedColumns = getApprovedColumns(approvedPlans);
  const declinedColumns = getDeclinedColumns(declinedRequests);

  return (
    <MapDataProvider>
      <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 2 }}>
        <ToastContainer 
          position="top-right"
          autoClose={8000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="plan requests tabs"
            sx={{
              '& .MuiTab-root': {
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
                minWidth: 200,
              }
            }}
          >
            <Tab 
              label={`Approved Plans (${approvedPlans.length})`} 
              id="plan-tab-0" 
              aria-controls="plan-tabpanel-0"
            />
            <Tab 
              label={`Declined Requests (${declinedRequests.length})`} 
              id="plan-tab-1" 
              aria-controls="plan-tabpanel-1"
            />
          </Tabs>
        </Box>
        
        {/* Approved Plans Tab */}
        <TabPanel value={tabValue} index={0}>
          {loadingApproved ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Box sx={{ ml: 2 }}>Loading approved plans...</Box>
            </Box>
          ) : (
            <>
              {approvedPlans.length > 0 ? (
                <IPDCFinalTable
                  ref={componentRef}
                  data={approvedPlans}
                  setData={setApprovedPlans}
                  selectedColumns={approvedColumns}
                  title="Approved Investor Plans"
                  attachmentLink=""
                  hasAttachment={false}
                />
              ) : (
                <Box sx={{ 
                  padding: '20px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '4px',
                  textAlign: 'center',
                  color: '#666',
                  minHeight: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  No approved plans found
                </Box>
              )}
            </>
          )}
        </TabPanel>
        
        {/* Declined Requests Tab */}
        <TabPanel value={tabValue} index={1}>
          {loadingDeclined ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Box sx={{ ml: 2 }}>Loading declined requests...</Box>
            </Box>
          ) : (
            <>
              {declinedRequests.length > 0 ? (
                <IPDCFinalTable
                  ref={componentRef}
                  data={declinedRequests}
                  setData={setDeclinedRequests}
                  selectedColumns={declinedColumns}
                  title="Declined Plan Requests"
                  attachmentLink=""
                  hasAttachment={false}
                />
              ) : (
                <Box sx={{ 
                  padding: '20px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '4px',
                  textAlign: 'center',
                  color: '#666',
                  minHeight: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  No declined requests found
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* Loading Dialog */}
        <Dialog open={loadingGeometry} maxWidth="xs">
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Box sx={{ mt: 2 }}>Loading parcel data...</Box>
          </DialogContent>
        </Dialog>

        {/* Conditional Plan Print Preview Dialog */}
        {}
        {openPreview && selectedFeature && (
          previewType === "approved" ? (
            <PlanPrintPreview3
              feature={selectedFeature}
              parkName={selectedParkName}
              investorName={selectedInvestorName}
              open={openPreview}
              onClose={handleClosePreview}
              onPrint={handlePrint}
            />
          ) : (
            <PlanPrintPreview4
              feature={selectedFeature}
              parkName={selectedParkName}
              investorName={selectedInvestorName}
              open={openPreview}
              onClose={handleClosePreview}
              onPrint={handlePrint}
            />
          )
        )}
      </Box>
    </MapDataProvider>
  );
}